import { useState } from 'react';
import { X, Plus } from 'lucide-react';
import { updateMemberInstruments } from '../../hooks/useBands';
import { INSTRUMENT_OPTIONS } from '../../lib/utils';

interface Props {
  memberId: string;
  memberName: string;
  currentInstruments: string[];
  onClose: () => void;
  onSuccess: () => void;
}

export default function EditInstrumentsModal({ memberId, memberName, currentInstruments, onClose, onSuccess }: Props) {
  const [instruments, setInstruments] = useState<string[]>(currentInstruments);
  const [customInstrument, setCustomInstrument] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  function toggleInstrument(instrument: string) {
    setInstruments((prev) =>
      prev.includes(instrument)
        ? prev.filter((i) => i !== instrument)
        : [...prev, instrument]
    );
  }

  function addCustom() {
    const trimmed = customInstrument.trim();
    if (trimmed && !instruments.includes(trimmed)) {
      setInstruments((prev) => [...prev, trimmed]);
      setCustomInstrument('');
    }
  }

  async function handleSave() {
    setLoading(true);
    setError('');
    const { error } = await updateMemberInstruments(memberId, instruments);
    if (error) {
      setError(error);
      setLoading(false);
    } else {
      onSuccess();
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-800 rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-800 sticky top-0 bg-gray-900">
          <div>
            <h2 className="text-lg font-semibold">Instruments & Vocals</h2>
            <p className="text-sm text-gray-400">{memberName}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-50">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {error && (
            <div className="bg-red-900/50 border border-red-700 text-red-200 px-3 py-2 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {INSTRUMENT_OPTIONS.map((inst) => (
              <button
                key={inst}
                type="button"
                onClick={() => toggleInstrument(inst)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                  instruments.includes(inst)
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-800 text-gray-400 hover:text-gray-50 hover:bg-gray-700'
                }`}
              >
                {inst}
              </button>
            ))}
          </div>

          {/* Custom instruments already added that aren't in the predefined list */}
          {instruments.filter((i) => !INSTRUMENT_OPTIONS.includes(i)).length > 0 && (
            <div className="flex flex-wrap gap-2">
              {instruments.filter((i) => !INSTRUMENT_OPTIONS.includes(i)).map((inst) => (
                <button
                  key={inst}
                  type="button"
                  onClick={() => toggleInstrument(inst)}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium bg-indigo-600 text-white transition"
                >
                  {inst}
                </button>
              ))}
            </div>
          )}

          <div className="flex gap-2">
            <input
              type="text"
              value={customInstrument}
              onChange={(e) => setCustomInstrument(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCustom())}
              placeholder="Add custom instrument..."
              className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <button
              type="button"
              onClick={addCustom}
              disabled={!customInstrument.trim()}
              className="bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-gray-50 px-3 py-2 rounded-lg transition"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-800 hover:bg-gray-700 text-gray-50 py-2 px-4 rounded-lg transition"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={loading}
              className="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white py-2 px-4 rounded-lg transition"
            >
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
